import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Plus, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import api, { getFileUrl, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import TouchUpModal from "@/components/patients/TouchUpModal";
import { TREATMENTS } from "@/constants/testIds";

const PAYMENT_METHOD_LABELS = { cash: "Efectivo", card: "Tarjeta", transfer: "Transferencia", other: "Otro" };
const REVIEW_STATUS_LABELS = { active: "Vigente", expired: "Vencida", used: "Utilizada" };

function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) + " \u00b7 " + d.toTimeString().slice(0, 5);
}

function formatDateOnly(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
}

function ZonesTable({ zones }) {
  if (!zones?.length) return <p className="text-sm text-muted-foreground">Sin zonas registradas.</p>;
  return (
    <table className="w-full text-sm" data-testid={TREATMENTS.detailZonesTable}>
      <thead>
        <tr className="text-left text-muted-foreground border-b border-border">
          <th className="py-1.5 font-medium">Zona</th>
          <th className="py-1.5 font-medium">Unidades</th>
          <th className="py-1.5 font-medium">Notas</th>
        </tr>
      </thead>
      <tbody>
        {zones.map((z, idx) => (
          <tr key={idx} className="border-b border-border/60 last:border-0">
            <td className="py-1.5 text-foreground">{z.zone_name}</td>
            <td className="py-1.5 text-foreground">{z.units != null ? z.units : "-"}</td>
            <td className="py-1.5 text-muted-foreground">{z.notes || "-"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PaymentLine({ paymentInfo }) {
  return (
    <p className="text-sm" data-testid={TREATMENTS.detailPaymentInfo}>
      <span className="text-muted-foreground">Pago: </span>
      {paymentInfo
        ? <span className="text-foreground font-medium">${paymentInfo.amount} · {PAYMENT_METHOD_LABELS[paymentInfo.method] || paymentInfo.method}{paymentInfo.provider === "square" ? " (Square)" : ""}</span>
        : <span className="text-muted-foreground">Sin pago registrado</span>}
    </p>
  );
}

export default function TreatmentDetailPage() {
  const { patientId, treatmentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [detail, setDetail] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [touchUpOpen, setTouchUpOpen] = useState(false);

  const canRegisterTouchUp = user?.role === "admin" || user?.role === "professional";

  const load = async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const [detailRes, clinicRes, profRes, productsRes] = await Promise.all([
        api.get(`/patients/${patientId}/treatments/${treatmentId}`),
        api.get("/clinic"),
        api.get("/team/professionals/list"),
        api.get("/inventory/products"),
      ]);
      setDetail(detailRes.data);
      setClinic(clinicRes.data);
      setProfessionals(profRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setForbidden(true);
      } else {
        toast({ title: "Error al cargar la ficha", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [patientId, treatmentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <p className="text-center text-muted-foreground py-16">Cargando ficha técnica...</p>;

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto text-center py-20">
        <Lock className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No tenes acceso a esta ficha.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(`/pacientes/${patientId}`)}>Volver</Button>
      </div>
    );
  }

  if (!detail) return null;

  return (
    <div className="space-y-6" data-testid={TREATMENTS.detailPage}>
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate(`/pacientes/${patientId}`)} data-testid={TREATMENTS.detailBackButton}>
          <ArrowLeft className="w-4 h-4" /> Volver a la ficha del paciente
        </Button>
        {/* Fix AI (2026-10-23): en el celular los botones pasan a la linea de abajo en vez de salirse de la pantalla */}
        <div className="flex flex-wrap items-center gap-2">
          {canRegisterTouchUp && (
            <Button variant="outline" onClick={() => setTouchUpOpen(true)} data-testid={TREATMENTS.detailRegisterTouchupButton}>
              <Plus className="w-4 h-4" /> Registrar retoque
            </Button>
          )}
          <Button onClick={() => window.print()} className="bg-brand hover:bg-brand-hover text-white" data-testid={TREATMENTS.detailPrintButton}>
            <Printer className="w-4 h-4" /> Imprimir / Guardar PDF
          </Button>
        </div>
      </div>

      <div className="print-area bg-white border border-border rounded-xl p-6 sm:p-10 max-w-3xl mx-auto space-y-8 print:border-0 print:shadow-none print:p-0 print:max-w-full">
        <div className="flex items-center justify-between border-b border-border pb-5">
          <div className="flex items-center gap-3">
            {clinic?.logo_url && <img src={getFileUrl(clinic.logo_url)} alt={clinic.name} className="w-12 h-12 object-contain" />}
            <div>
              <p className="font-serif text-xl text-foreground">{clinic?.name}</p>
              <p className="text-xs text-muted-foreground">{[clinic?.address, clinic?.phone].filter(Boolean).join(" \u00b7 ")}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Ficha técnica del tratamiento</p>
            <p className="text-xs text-muted-foreground">Emitida el {formatDateOnly(new Date().toISOString())}</p>
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Paciente</p>
          <p className="font-serif text-2xl text-foreground">{detail.patient_name}</p>
          {detail.patient_phone && <p className="text-sm text-muted-foreground">{detail.patient_phone}</p>}
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-serif text-xl text-foreground">{detail.service_name || detail.product_notes || "Tratamiento"}</h2>
            {detail.review_status && (
              <Badge data-testid={TREATMENTS.detailReviewStatus} className="border font-normal bg-brand-light text-brand border-brand/30">
                Revisión: {REVIEW_STATUS_LABELS[detail.review_status]} {detail.review_due_date ? `(vence ${formatDateOnly(detail.review_due_date)})` : ""}
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <p><span className="text-muted-foreground">Profesional: </span><span className="text-foreground font-medium">{detail.professional_name}</span></p>
            <p><span className="text-muted-foreground">Fecha: </span><span className="text-foreground font-medium">{formatDateTime(detail.treatment_date)}</span></p>
          </div>
          {detail.primary_product_name && (
            <p className="text-sm"><span className="text-muted-foreground">Producto: </span><span className="text-foreground font-medium">{detail.primary_product_name} · Total: {detail.total_units} {detail.primary_product_unit}</span></p>
          )}
          <ZonesTable zones={detail.zones} />
          {detail.products_used?.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Otros insumos: {detail.products_used.filter((p) => p.product_id !== detail.primary_product_id).map((p) => `${p.product_name} (${p.quantity} ${p.unit})`).join(", ") || "-"}
            </p>
          )}
          <PaymentLine paymentInfo={detail.payment_info} />
          {detail.observations && (
            <div>
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Observaciones</p>
              <p className="text-sm text-foreground whitespace-pre-line">{detail.observations}</p>
            </div>
          )}
        </div>

        {detail.touchups?.length > 0 && (
          <div className="border-t border-border pt-5 space-y-4">
            <h3 className="text-xs uppercase tracking-[0.1em] text-muted-foreground font-medium">Retoques</h3>
            {detail.touchups.map((tu) => (
              <div key={tu.id} className="border border-border rounded-lg p-4 space-y-2" data-testid={`${TREATMENTS.detailTouchupItemPrefix}${tu.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-sm font-medium text-foreground">{formatDateTime(tu.touchup_date)} \u00b7 {tu.professional_name}</p>
                  {tu.included_in_review ? (
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border font-normal">Sin cargo (revisión)</Badge>
                  ) : (
                    <PaymentLine paymentInfo={tu.payment_info} />
                  )}
                </div>
                {tu.primary_product_name && (
                  <p className="text-sm"><span className="text-muted-foreground">Producto: </span>{tu.primary_product_name} · Total: {tu.total_units} {tu.primary_product_unit}</p>
                )}
                <ZonesTable zones={tu.zones} />
                {tu.observations && <p className="text-sm text-muted-foreground whitespace-pre-line">{tu.observations}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <TouchUpModal
        open={touchUpOpen}
        onOpenChange={setTouchUpOpen}
        patientId={patientId}
        treatment={detail}
        professionals={professionals}
        products={products}
        currentUser={user}
        onCreated={() => { toast({ title: "Retoque registrado" }); setTouchUpOpen(false); load(); }}
      />
    </div>
  );
}
